import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { ModalComponent } from '../../../../shared/components/ui/modal/modal.component';
import {
  MeetingCancellationReasonResponse,
  MeetingRescheduleLogRequest,
  MeetingResponse,
  MeetingStatusResponse,
} from '../../models/meeting.model';
import { MeetingApiService } from '../../services/meeting-api.service';
import { MeetingCancellationReasonApiService } from '../../services/meeting-cancellation-reason-api.service';
import { MeetingStatusApiService } from '../../services/meeting-status-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { findCancelStatusId, toMeetingRequest } from '../meeting-action.util';

/**
 * Modal de CANCELACIÓN de reunión — reutilizable desde lista, calendario y kanban.
 *
 * Compone las operaciones existentes:
 *  - PUT  /api/meetings/{id}                        → aplica el estado de cancelación + motivo
 *  - POST /api/meetings/{id}/reschedule-logs (cancel) → deja el registro en el historial
 *
 * @Input targetStatusId — estado destino explícito (columna de kanban "Cancelado").
 *   Si no se pasa, se busca en el catálogo un estado cuyo nombre represente cancelación.
 */
@Component({
  selector: 'app-meeting-cancel-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './meeting-cancel-dialog.component.html',
})
export class MeetingCancelDialogComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() meeting: MeetingResponse | null = null;
  @Input() targetStatusId?: number;

  /** Modal cerrado sin cancelar (X / backdrop / botón "Volver"). */
  @Output() cancelled = new EventEmitter<void>();
  /** Cancelación aplicada correctamente. Emite la reunión actualizada. */
  @Output() confirmed = new EventEmitter<MeetingResponse>();

  reasons: MeetingCancellationReasonResponse[] = [];
  private statuses: MeetingStatusResponse[] = [];
  loading = false;
  saving = false;

  // Formulario
  selectedReasonId: number | 'other' | null = null;
  customReason = '';

  constructor(
    private meetingApi: MeetingApiService,
    private reasonApi: MeetingCancellationReasonApiService,
    private statusApi: MeetingStatusApiService,
    private authService: AuthService,
    private toast: ToastService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.meeting) {
      this.resetForm();
      this.loadCatalogs();
    }
  }

  private resetForm(): void {
    this.selectedReasonId = null;
    this.customReason = '';
    this.saving = false;
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  private loadCatalogs(): void {
    this.loading = true;
    const businessId = this.businessId;
    this.reasonApi
      .listActive(businessId)
      .pipe(catchError(() => of([] as MeetingCancellationReasonResponse[])))
      .subscribe((data) => {
        this.reasons = data;
        this.loading = false;
      });
    this.statusApi
      .listActive(businessId)
      .pipe(catchError(() => of([] as MeetingStatusResponse[])))
      .subscribe((data) => (this.statuses = data));
  }

  get isOther(): boolean {
    return this.selectedReasonId === 'other';
  }

  onClose(): void {
    if (this.saving) return;
    this.cancelled.emit();
  }

  confirmar(): void {
    if (!this.meeting?.id || this.saving) return;

    if (this.selectedReasonId == null) {
      this.toast.error({ title: 'Motivo requerido', message: 'Seleccione un motivo de cancelación.' });
      return;
    }
    if (this.isOther && !this.customReason.trim()) {
      this.toast.error({ title: 'Motivo requerido', message: 'Especifique el motivo de cancelación.' });
      return;
    }

    const meeting = this.meeting;
    const meetingId = meeting.id;
    const businessId = this.businessId;

    const idCancellationReason = this.isOther ? undefined : (this.selectedReasonId as number);
    const reasonText = this.isOther
      ? this.customReason.trim()
      : this.reasons.find((r) => r.id === this.selectedReasonId)?.name ?? '';

    const cancelStatusId = this.targetStatusId ?? findCancelStatusId(this.statuses);

    const body = toMeetingRequest(meeting);
    if (cancelStatusId != null) body.idMeetingStatus = cancelStatusId;
    body.idCancellationReason = idCancellationReason;
    body.cancellationReasonText = reasonText;

    this.saving = true;
    this.meetingApi.updateMeeting(meetingId, businessId, body).subscribe({
      next: (updated) => {
        const currentUserId = this.authService.getCurrentUser()?.id;
        const logReq: MeetingRescheduleLogRequest = {
          idMeeting: meetingId,
          action: 'cancel',
          originalDatetimeStart: meeting.datetimeStart,
          originalDatetimeEnd: meeting.datetimeEnd ?? meeting.datetimeStart,
          idCancellationReason,
          reasonText,
          initiatedBy: 'host',
          userInitiatedId: currentUserId != null ? +currentUserId : undefined,
          rescheduleNumber: (meeting.rescheduleCount ?? 0) + 1,
        };
        this.meetingApi
          .createRescheduleLog(meetingId, logReq)
          .pipe(catchError(() => of(null)))
          .subscribe(() => {
            this.saving = false;
            this.toast.success({
              title: 'Reunión cancelada',
              message: `Se canceló la reunión "${updated.title}".`,
            });
            this.confirmed.emit(updated);
          });
      },
      error: (err) => {
        this.saving = false;
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'No se pudo cancelar la reunión.',
        });
      },
    });
  }
}
